/**
 * League service - the only place that mutates db.league. Every League HQ
 * Settings edit goes through here so persistence and change notification stay
 * centralized.
 */
import type {
  BrandingConfig,
  LeagueCommunicationSettings,
  LeagueConfig,
  LeagueEventDefaults,
} from '../models';
import { getDb, updateDb } from './store';

type LeagueEventDefaultsPatch = Partial<Omit<LeagueEventDefaults, 'modules' | 'season'>> & {
  season?: Partial<LeagueEventDefaults['season']>;
};

export const getLeague = (): LeagueConfig => getDb().league;

export const updateLeague = (
  patch: Partial<Omit<LeagueConfig, 'branding' | 'eventDefaults' | 'communicationSettings'>>,
): void => {
  updateDb((db) => ({ ...db, league: { ...db.league, ...patch } }));
};

export const updateBranding = (patch: Partial<BrandingConfig>): void => {
  updateDb((db) => ({
    ...db,
    league: { ...db.league, branding: { ...db.league.branding, ...patch } },
  }));
};

export const updateEventDefaults = (patch: LeagueEventDefaultsPatch): void => {
  updateDb((db) => {
    const eventDefaults = {
      ...db.league.eventDefaults,
      ...patch,
      season: { ...db.league.eventDefaults.season, ...(patch.season ?? {}) },
    };
    return {
      ...db,
      league: {
        ...db.league,
        ageGroups: patch.divisionLabels ?? db.league.ageGroups,
        eventDefaults,
      },
    };
  });
};

export const updateCommunicationSettings = (patch: Partial<LeagueCommunicationSettings>): void => {
  updateDb((db) => ({
    ...db,
    league: {
      ...db.league,
      communicationSettings: {
        ...db.league.communicationSettings,
        ...patch,
        templates: patch.templates ?? db.league.communicationSettings.templates,
      },
    },
  }));
};

/** Reset to factory defaults. */
export const resetBranding = (defaults: BrandingConfig): void => {
  updateDb((db) => ({ ...db, league: { ...db.league, branding: { ...defaults } } }));
};
